import { inject, Injector, runInInjectionContext } from "@angular/core";
import { CanActivateFn, GuardResult, RedirectCommand, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { AuthenticationService } from "src/app/core/authentication/authentication.service";
import { AuthorizationService } from "src/app/core/authentication/authorization.service";
import { isLocal } from "src/app/core/constants/is-development";
import { EnvironmentId } from "src/app/core/enums/environment-id.enum";
import { Role } from "src/app/core/enums/role.enum";
import { joinPromiseRecord } from "src/app/core/functions/join-promise-record";
import { maybeAsyncToPromise } from "src/app/core/functions/maybe-async-to";
import { authGuard } from "src/app/core/guards";
import { environment } from "src/environments/environment";

/**
 * Guard that ensures the user is always authenticated on deployed non-productive environments.
 *
 * If the user is not authenticated, it runs the authGuard to initiate the sign-in process.
 *
 * If the user does not have the required role, it redirects to an error page with a relevant message.
 *
 * If it is a productive environment or local development, the guard allows access without restrictions.
 */
export const devAccessGuard: CanActivateFn =
  environment.id === EnvironmentId.Production || isLocal
    ? () => <const>true
    : async (...args): Promise<GuardResult> => {
        const injector = inject(Injector);
        const auth = injector.get(AuthenticationService);
        if (!auth.isAuthenticated) {
          // Run authGuard
          return await maybeAsyncToPromise(runInInjectionContext(injector, () => authGuard(...args)));
        }

        if (!injector.get(AuthorizationService).hasRole(Role.User)) {
          const translate = injector.get(TranslateService);

          const { statusText, message } = await joinPromiseRecord({
            statusText: firstValueFrom<unknown>(translate.get("ERROR.FORBIDDEN")).catch(() => "Forbidden"),
            message: firstValueFrom<unknown>(translate.get("ERROR.DEV_ACCESS_MESSAGE")).catch(
              () => "This is a non-productive environment. To get access, please contact the product owner of myRegistration."
            ),
          });

          return new RedirectCommand(injector.get(Router).createUrlTree(["/error"], { queryParams: { statusText, message, button: ["auth"] } }), {
            skipLocationChange: true,
          });
        }

        return true;
      };
